import React, { FC, MouseEvent, useState } from 'react'

import MenuIcon from '@mui/icons-material/Menu'
import { Menu, MenuItem } from '@mui/material'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Typography from '@mui/material/Typography'
import { ToolbarPropsOverrides } from '@mui/x-data-grid'
import { GridToolbarProps } from '@mui/x-data-grid/components/toolbar'

import { showNotification } from 'utils/showNotification'

import { useDeleteBookingsCardsMutation } from 'services/bookings/api'
import { BookingsCard } from 'services/bookings/types'

import ConfirmModal from 'components/common/ConfirmModal'
import TableToolbar from 'components/table/TableToolbar'

import { CardsTabActions } from 'pages/bookings/CardsTab/index'

import EditCardModal from '../EditCardModal'

interface Props extends Partial<GridToolbarProps & ToolbarPropsOverrides> {
  modal: CardsTabActions
  setModal: React.Dispatch<React.SetStateAction<CardsTabActions>>
  singleRow: any
}

const BookingsCardsTableToolbar: FC<Props> = ({
  selectedRows,
  searchValue,
  onExportButtonClick,
  onSearchChange,
  modal,
  setModal,
  singleRow,
}) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)

  const [deleteCards, { isLoading }] = useDeleteBookingsCardsMutation()

  const handleOpenMenu = (event: MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget)
  }

  const handleCloseMenu = () => {
    setAnchorEl(null)
  }

  const confirmDelete = async () => {
    if (modal === 'DELETE') {
      deleteCards({ cardIds: selectedRows.map((item: BookingsCard) => item.id) })
        .unwrap()
        .then(() => {
          showNotification(
            selectedRows.length === 1
              ? `Card successfully deactivated`
              : `Selected Cards successfully deactivated`,
            'success'
          )
          setModal('')
        })
        .catch(() => showNotification('Error. Try again later', 'error'))
    } else {
      deleteCards({ cardIds: [singleRow.id] })
        .unwrap()
        .then(() => {
          showNotification(`Card successfully deactivated`, 'success')
          setModal('')
        })
        .catch(() => showNotification('Error. Try again later', 'error'))
    }
    setModal('')
  }

  const confirmMessage =
    modal === 'DELETE'
      ? `Are you sure you want to deactivate selected card${selectedRows.length > 1 ? 's' : ''}?`
      : 'Are you sure you want to deactivate selected card?'

  return (
    <>
      <TableToolbar
        searchValue={searchValue}
        onExportButtonClick={onExportButtonClick}
        onChange={onSearchChange}
        rightContent={
          <Box>
            <Button size="small" startIcon={<MenuIcon />} onClick={handleOpenMenu}>
              Actions
            </Button>
            <Box sx={{ flexGrow: 0, display: 'flex', alignItems: 'center' }}>
              <Menu
                sx={{ mt: '45px' }}
                id="menu-toolbar"
                anchorEl={anchorEl}
                anchorOrigin={{
                  vertical: 'top',
                  horizontal: 'right',
                }}
                keepMounted
                transformOrigin={{
                  vertical: 'top',
                  horizontal: 'right',
                }}
                open={!!anchorEl}
                onClose={handleCloseMenu}
              >
                <MenuItem
                  onClick={() => {
                    setModal('ADD')
                    handleCloseMenu()
                  }}
                >
                  <Typography textAlign="center">Add Card</Typography>
                </MenuItem>
                {selectedRows.length === 1 ? (
                  <MenuItem
                    onClick={() => {
                      setModal('EDIT')
                      handleCloseMenu()
                    }}
                  >
                    <Typography textAlign="center">Edit Card</Typography>
                  </MenuItem>
                ) : null}
                {selectedRows.length >= 1 ? (
                  <MenuItem
                    onClick={() => {
                      setModal('DELETE')
                      handleCloseMenu()
                    }}
                  >
                    <Typography textAlign="center">
                      Deactivate Card{selectedRows.length > 1 ? 's' : ''}
                    </Typography>
                  </MenuItem>
                ) : null}
              </Menu>
            </Box>
          </Box>
        }
      />

      {modal === 'ADD' && <EditCardModal onClose={() => setModal('')} />}
      {(modal === 'EDIT' || modal === 'EDIT_SINGLE') && (
        <EditCardModal
          card={modal === 'EDIT' ? selectedRows[0] : singleRow}
          onClose={() => setModal('')}
        />
      )}
      {(modal === 'DELETE' || modal === 'DELETE_SINGLE') && (
        <ConfirmModal
          open={true}
          onConfirm={confirmDelete}
          loading={isLoading}
          onClose={() => {
            setModal('')
          }}
        >
          <Typography
            sx={{
              fontSize: '24px',
              fontWeight: 600,
              textAlign: 'center',
            }}
          >
            {confirmMessage}
          </Typography>
        </ConfirmModal>
      )}
    </>
  )
}

export default BookingsCardsTableToolbar
